/**
 * @fileoverview Interfaz IAnalyticsRepository - Puerto secundario para servicios de análisis
 * Define contratos para adaptadores de analíticas conforme a patrón Ports & Adapters
 */

/**
 * @interface IAnalyticsRepository
 * @description Define el contrato que deben implementar los repositorios de analíticas
 * Actúa como puerto secundario en arquitectura hexagonal para análisis de datos
 * @abstract
 */
export default class IAnalyticsRepository {
  constructor() {
    if (this.constructor === IAnalyticsRepository) {
      throw new Error('IAnalyticsRepository es una interfaz abstracta y no puede ser instanciada directamente');
    }
  }

  /**
   * Obtiene un reporte analítico según parámetros especificados
   * @param {string} reportType - Tipo de reporte a obtener
   * @param {Object} params - Parámetros de filtrado, temporalidad y dimensiones
   * @param {string} params.timeRange - Rango temporal ('today', 'last_7_days', etc.)
   * @param {Date} [params.startDate] - Fecha inicial para rangos personalizados
   * @param {Date} [params.endDate] - Fecha final para rangos personalizados
   * @param {Object} [params.filters] - Filtros adicionales a aplicar
   * @param {Array<string>} [params.dimensions] - Dimensiones para la agregación
   * @param {Array<string>} [params.metrics] - Métricas a calcular
   * @returns {Promise<Object>} Datos del reporte analítico solicitado
   * @throws {Error} Si ocurre un error durante la consulta
   * @abstract
   */
  async getReport(reportType, params) {
    throw new Error('getReport debe ser implementado por las clases derivadas');
  }

  /**
   * Obtiene tendencias para un período determinado
   * @param {string} entity - Entidad sobre la que calcular tendencias (ventas, productos, clientes)
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.timeRange - Rango temporal
   * @param {string} params.interval - Intervalo de agregación ('day', 'week', 'month')
   * @param {Object} [params.filters] - Filtros adicionales
   * @returns {Promise<Array>} Serie temporal con las tendencias calculadas
   * @throws {Error} Si ocurre un error durante el cálculo de tendencias
   * @abstract
   */
  async getTrends(entity, params) {
    throw new Error('getTrends debe ser implementado por las clases derivadas');
  }

  /**
   * Obtiene indicadores clave de rendimiento (KPIs)
   * @param {Array<string>} kpiNames - Nombres de los KPI a obtener
   * @param {Object} params - Parámetros de consulta
   * @param {string} params.timeRange - Rango temporal
   * @param {Object} [params.filters] - Filtros adicionales
   * @param {boolean} [params.compareWithPrevious=true] - Incluir comparación con período anterior
   * @returns {Promise<Object>} Valores de KPIs con sus métricas asociadas
   * @throws {Error} Si los KPIs solicitados no están disponibles
   * @abstract
   */
  async getKpis(kpiNames, params) {
    throw new Error('getKpis debe ser implementado por las clases derivadas');
  }

  /**
   * Obtiene anomalías detectadas en datos históricos
   * @param {string} metricName - Métrica sobre la cual detectar anomalías
   * @param {Object} params - Parámetros para la detección
   * @param {string} params.timeRange - Rango temporal a evaluar
   * @param {number} [params.sensitivityLevel=3] - Nivel de sensibilidad (1-5)
   * @param {Object} [params.filters] - Filtros adicionales
   * @returns {Promise<Array>} Anomalías detectadas con detalles y confianza
   * @throws {Error} Si ocurre un error durante la detección de anomalías
   * @abstract
   */
  async detectAnomalies(metricName, params) {
    throw new Error('detectAnomalies debe ser implementado por las clases derivadas');
  }

  /**
   * Genera un reporte programado
   * @param {Object} reportConfig - Configuración del reporte
   * @param {string} reportConfig.type - Tipo de reporte
   * @param {string} reportConfig.format - Formato de salida (json, csv, pdf)
   * @param {Object} reportConfig.params - Parámetros específicos del reporte
   * @param {Object} [reportConfig.schedule] - Configuración de programación (cron, recurrencia)
   * @returns {Promise<string>} ID del reporte generado o programado
   * @throws {Error} Si la configuración es inválida
   * @abstract
   */
  async scheduleReport(reportConfig) {
    throw new Error('scheduleReport debe ser implementado por las clases derivadas');
  }

  /**
   * Obtiene dashboard completo con múltiples widgets
   * @param {string} dashboardId - Identificador del dashboard
   * @param {Object} [params] - Parámetros para personalización
   * @param {Object} [params.filters] - Filtros globales para el dashboard
   * @param {string} [params.timeRange='last_30_days'] - Rango temporal
   * @returns {Promise<Object>} Configuración y datos para todos los widgets
   * @throws {Error} Si el dashboard solicitado no existe
   * @abstract
   */
  async getDashboard(dashboardId, params = {}) {
    throw new Error('getDashboard debe ser implementado por las clases derivadas');
  }

  /**
   * Guarda preferencias del usuario para visualizaciones
   * @param {string} userId - ID del usuario
   * @param {Object} preferences - Preferencias a guardar
   * @returns {Promise<boolean>} Resultado de la operación
   * @throws {Error} Si las preferencias no pueden ser guardadas
   * @abstract
   */
  async saveUserPreferences(userId, preferences) {
    throw new Error('saveUserPreferences debe ser implementado por las clases derivadas');
  }

  /**
   * Realiza una pregunta en lenguaje natural al sistema de analytics
   * @param {string} question - Pregunta en lenguaje natural
   * @param {Object} [context] - Contexto adicional (usuario, fecha, etc.)
   * @returns {Promise<Object>} Respuesta con datos, gráficos y explicaciones
   * @throws {Error} Si la pregunta no puede ser interpretada
   * @abstract
   */
  async askNaturalLanguageQuestion(question, context = {}) {
    throw new Error('askNaturalLanguageQuestion debe ser implementado por las clases derivadas');
  }
}

// Exportado con ES Modules (export default) al inicio de la clase
